/**
 * Document Generation Store (Pinia)
 * 자동 저장/복원 기능 포함
 */

import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import type {
  DocGenState,
  DraftSummary,
  VariableField,
  EntityForm,
  GeneratedDoc,
  ConversationMessage,
  TemplateInfo,
  EntityItem,
} from '../types/docGen.types';
import { useConversation } from '../composables/useConversation';
import { docGenApi } from '../api/docGenApi';
import { createEmptyIncorporationQA } from '@/features/startTask/preset_task/my/incorporation/types/incorporation.types';
import type { IncorporationQAState } from '@/features/startTask/preset_task/my/incorporation/types/incorporation.types';

export const useDocGenStore = defineStore('docGen', () => {
  const { createAIMessage, createUserMessage } = useConversation();

  // ── State ────────────────────────────────────────────────────
  const currentStep = ref(1);
  const docType = ref('');
  const taskName = ref('');
  const taskId = ref<number | null>(null);
  const sessionEntityCd = ref('');

  // Templates (multi-template)
  const templates = ref<TemplateInfo[]>([]);
  const templateName = computed(() => {
    if (templates.value.length === 0) return '';
    if (templates.value.length === 1) return templates.value[0].fileName;
    return `${templates.value.length} templates`;
  });

  // Variables
  const variableSchema = ref<VariableField[]>([]);
  const sectionSummaries = ref<Record<string, string>>({});

  // Section summary sources (cache/gpt/short)
  const sectionSummarySources = ref<Record<string, string>>({});

  // Multi-entity
  const entities = ref<EntityForm[]>([]);
  const activeEntityIndex = ref(0);

  // 챗봇에서 선택된 entity profile (폼 필드 제안용)
  const selectedEntityProfile = ref<EntityItem | null>(null);

  // CDD 대상자 선택 시 폼 필드 제안 데이터 (varKey → displayValue)
  const cddHintData = ref<Record<string, string>>({});

  // Incorporation Q&A 상태
  const incorporationQA = ref<IncorporationQAState>(createEmptyIncorporationQA());
  const incorporationMappedDocs = ref<any[]>([]);

  // Variable Value History
  const variableHistory = ref<Array<{
    saveId: number;
    taskId: number;
    taskTitle: string;
    entityCd: string;
    entityName: string;
    fileName: string;
    createdBy: string;
    dataJson: Record<string, any>;
    date: string;
  }>>([]);
  const loadingHistory = ref(false);

  // Generation
  const generatedDocs = ref<GeneratedDoc[]>([]);
  const editorVisited = ref(false);
  const hasTSign = ref(false);
  const tSignAllComplete = ref(false);
  const eFilingCompleted = ref(false);

  // Skipped fields — 글로벌 (하위호환) + entity별 개별 관리
  const skippedFields = ref<Set<string>>(new Set());
  // 현재 활성 entity의 skippedFields (entity별 우선, 없으면 글로벌 fallback)
  const activeSkippedFields = computed(() => {
    const entity = entities.value[activeEntityIndex.value];
    if (entity?.skippedFields) return new Set(entity.skippedFields);
    return skippedFields.value;
  });

  // Conversation
  const conversationMessages = ref<ConversationMessage[]>([
    createAIMessage(1),
  ]);

  // Draft persistence (multi-draft)
  const currentDraftId = ref<string | null>(null);
  const drafts = ref<DraftSummary[]>([]);
  const loadingDrafts = ref(false);
  const savedAt = ref<string | undefined>();
  const saving = ref(false);

  // formData 변경 시 자동 저장 (드래프트 로드 상태에서만)
  function autoSaveIfDraft() {
    if (!currentDraftId.value || saving.value) return;
    saveDraft();
  }
  const restoring = ref(false);
  const hasRestoredDraft = ref(false);

  // ── Getters ──────────────────────────────────────────────────
  const activeEntity = computed(() => entities.value[activeEntityIndex.value]);
  const entityCount = computed(() => entities.value.length);
  const hasVariables = computed(() => variableSchema.value.length > 0);
  const canGenerate = computed(() =>
    entities.value.length > 0 &&
    entities.value.every((e) => Object.keys(e.formData).length > 0)
  );

  // ── Actions ──────────────────────────────────────────────────

  function setDocType(type: string) {
    currentDraftId.value = null;
    docType.value = type;
    conversationMessages.value.push(
      createUserMessage(1, `Category: ${type}`),
    );
    goToStep(2);
  }

  function addTemplate(info: TemplateInfo) {
    // Duplicate check
    const dup = templates.value.some((t) => {
      if (info.source === 'existing' && t.source === 'existing') return t.templateId === info.templateId;
      if (info.source === 'upload' && t.source === 'upload') return t.uploadId === info.uploadId;
      return false;
    });
    if (!dup) {
      templates.value.push(info);
    }
  }

  function removeTemplate(index: number) {
    const removed = templates.value[index];
    templates.value.splice(index, 1);
    // 기존 entity의 per-entity templates에서도 동일 템플릿 제거
    if (removed) {
      for (const entity of entities.value) {
        if (!entity.templates) continue;
        const eidx = entity.templates.findIndex(t =>
          (t.templateId && t.templateId === removed.templateId) ||
          (t.uploadId && t.uploadId === removed.uploadId),
        );
        if (eidx !== -1) entity.templates.splice(eidx, 1);
      }
    }
  }

  function confirmTemplates() {
    const names = templates.value.map((t) => t.fileName).join(', ');
    conversationMessages.value.push(
      createUserMessage(2, `Selected ${templates.value.length} template(s): ${names}`),
    );
    goToStep(3);
  }

  function setVariableSchema(vars: VariableField[]) {
    variableSchema.value = vars;

    // Initialize first entity form
    if (entities.value.length === 0) {
      addEntityForm();
    }

    conversationMessages.value.push(
      createUserMessage(3, `Extracted ${vars.length} variable(s)`),
    );
    goToStep(4);
  }

  function addEntityForm(sourceFormData?: Record<string, any>) {
    const formData: Record<string, any> = {};
    for (const v of variableSchema.value) {
      formData[v.varKey] = sourceFormData?.[v.varKey] ?? v.defaultValue ?? '';
    }
    entities.value.push({
      id: crypto.randomUUID(),
      formData,
      skippedFields: [],
      templates: templates.value.map(t => ({ ...t })),
    });
    activeEntityIndex.value = entities.value.length - 1;
  }

  function removeEntityForm(index: number) {
    if (entities.value.length <= 1) return;
    entities.value.splice(index, 1);
    if (activeEntityIndex.value >= entities.value.length) {
      activeEntityIndex.value = entities.value.length - 1;
    }
  }

  function removeEntityTemplate(entityIndex: number, templateIndex: number) {
    const entity = entities.value[entityIndex];
    if (entity?.templates && entity.templates.length > 1) {
      entity.templates.splice(templateIndex, 1);
    }
  }

  function updateEntityFormData(entityIndex: number, varKey: string, value: any) {
    if (entities.value[entityIndex]) {
      entities.value[entityIndex].formData[varKey] = value;
      autoSaveIfDraft();
    }
  }

  /**
   * 반복부([[if NAME]]) 셀 값 기록 — formData[sectionKey] 배열의 rowIndex 행에 varKey 값을 채운다.
   * 챗봇 피커(주소/날짜/텍스트 등)에서 반복부 셀을 타깃으로 값 배치할 때 사용.
   */
  function updateEntityRepeatingCell(
    entityIndex: number,
    sectionKey: string,
    rowIndex: number,
    varKey: string,
    value: any,
  ) {
    const entity = entities.value[entityIndex];
    if (!entity) return;
    const src = entity.formData[sectionKey];
    const arr = Array.isArray(src) ? [...src] : [];
    while (arr.length <= rowIndex) arr.push({});
    arr[rowIndex] = { ...(arr[rowIndex] || {}), [varKey]: value };
    entity.formData[sectionKey] = arr;
    autoSaveIfDraft();
  }

  function updateEntityInfo(entityIndex: number, key: string, name: string) {
    if (entities.value[entityIndex]) {
      entities.value[entityIndex].entityKey = key;
      entities.value[entityIndex].entityName = name;
    }
  }

  function setGeneratedDocs(docs: GeneratedDoc[]) {
    generatedDocs.value = docs;
  }

  function skipField(varKey: string, skipped: boolean, entityIndex?: number) {
    const idx = entityIndex ?? activeEntityIndex.value;
    const entity = entities.value[idx];
    if (entity) {
      // entity별 skippedFields 관리
      const current = new Set(entity.skippedFields || []);
      if (skipped) current.add(varKey);
      else current.delete(varKey);
      entity.skippedFields = Array.from(current);
    } else {
      // entity 없는 경우 글로벌 fallback
      const updated = new Set(skippedFields.value);
      if (skipped) updated.add(varKey);
      else updated.delete(varKey);
      skippedFields.value = updated;
    }
  }

  function goToStep(step: number) {
    currentStep.value = step;
    // Add AI message for the new step
    const lastAI = conversationMessages.value
      .filter((m) => m.role === 'ai')
      .pop();
    if (!lastAI || lastAI.stepId !== step) {
      conversationMessages.value.push(createAIMessage(step));
    }
  }

  function goBack() {
    if (currentStep.value > 1) {
      // Step 3은 자동 로딩 화면이므로 Back 시 스킵 → Step 2로 이동
      const target = currentStep.value === 4 ? 2 : currentStep.value - 1;
      goToStep(target);
    }
  }

  function proceedToMultiEntity() {
    conversationMessages.value.push(
      createUserMessage(4, `Variable input complete (${entities.value.length} entities)`),
    );
    goToStep(5);
  }

  function proceedToGenerate() {
    conversationMessages.value.push(
      createUserMessage(5, `Ready to generate documents for ${entities.value.length} entities`),
    );
    goToStep(6);
  }

  function reset() {
    currentDraftId.value = null;
    currentStep.value = 1;
    docType.value = '';
    taskName.value = '';
    taskId.value = null;
    templates.value = [];
    variableSchema.value = [];
    sectionSummaries.value = {};
    sectionSummarySources.value = {};
    entities.value = [];
    activeEntityIndex.value = 0;
    generatedDocs.value = [];
    editorVisited.value = false;
    hasTSign.value = false;
    tSignAllComplete.value = false;
    eFilingCompleted.value = false;
    skippedFields.value = new Set();
    selectedEntityProfile.value = null;
    cddHintData.value = {};
    variableHistory.value = [];
    conversationMessages.value = [createAIMessage(1)];
    incorporationQA.value = createEmptyIncorporationQA();
    incorporationMappedDocs.value = [];
    savedAt.value = undefined;
    hasRestoredDraft.value = false;

    // 서버 드래프트는 삭제하지 않음 → 목록 리로드
    loadDraftList();
  }

  // ── Save Draft (수동) ──────────────────────────────────────

  /**
   * 수동 임시저장 (타이틀 optional)
   */
  async function saveDraft(draftTitle?: string) {
    if (!currentDraftId.value) {
      currentDraftId.value = crypto.randomUUID();
    }
    try {
      saving.value = true;
      // widget 정보는 직렬화에서 제외 (함수/컴포넌트 참조 포함 가능)
      const cleanMessages = conversationMessages.value.map((m) => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
        stepId: m.stepId,
      }));

      const state = {
        draftId: currentDraftId.value!,
        currentStep: currentStep.value,
        docType: docType.value,
        taskName: draftTitle || taskName.value,
        templates: templates.value,
        variableSchema: variableSchema.value,
        entities: entities.value,
        activeEntityIndex: activeEntityIndex.value,
        generatedDocs: [] as any[],
        conversationMessages: cleanMessages,
        skippedFields: Array.from(skippedFields.value),
      };

      const res = await docGenApi.saveState(state as any);
      if (res?.success) {
        savedAt.value = res.saved_at || new Date().toISOString();
        if (draftTitle) taskName.value = draftTitle;
        loadDraftList();
      }
    } catch (err) {
      console.warn('Save draft failed:', err);
    } finally {
      saving.value = false;
    }
  }

  /**
   * Variable Value History 로드
   */
  async function loadVariableHistory(tmpId: number, entityCd?: string) {
    loadingHistory.value = true;
    try {
      const res = await docGenApi.getVariableHistory({ tmpId, entityCd });
      if (res?.success) {
        variableHistory.value = res.history || [];
      }
    } catch (err) {
      console.warn('Variable history load failed:', err);
      variableHistory.value = [];
    } finally {
      loadingHistory.value = false;
    }
  }

  /**
   * 드래프트 목록 로드
   */
  async function loadDraftList() {
    loadingDrafts.value = true;
    try {
      const res = await docGenApi.getDrafts();
      if (res?.success) {
        drafts.value = res.drafts || [];
      }
    } catch (err) {
      console.warn('Draft list load failed:', err);
    } finally {
      loadingDrafts.value = false;
    }
  }

  /**
   * 특정 드래프트 로드 → 복원
   */
  async function loadDraft(draftId: string) {
    restoring.value = true;
    try {
      const res = await docGenApi.loadState(draftId);
      if (res?.success && res.state) {
        fromJSON(res.state);
        currentDraftId.value = draftId;
        hasRestoredDraft.value = true;
        return true;
      }
      return false;
    } catch (err) {
      console.warn('Draft load failed:', err);
      return false;
    } finally {
      restoring.value = false;
    }
  }

  /**
   * 특정 드래프트 삭제
   */
  async function deleteDraft(draftId: string) {
    try {
      await docGenApi.deleteState(draftId);
      drafts.value = drafts.value.filter((d) => d.draftId !== draftId);
    } catch (err) {
      console.warn('Draft delete failed:', err);
    }
  }

  /**
   * 레거시 호환: tryRestore → loadDraftList로 대체
   */
  async function tryRestore(): Promise<boolean> {
    await loadDraftList();
    return drafts.value.length > 0;
  }

  // ── Serialize / Restore ──────────────────────────────────────
  function toJSON(): DocGenState {
    return {
      currentStep: currentStep.value,
      docType: docType.value,
      templates: templates.value,
      variableSchema: variableSchema.value,
      entities: entities.value,
      activeEntityIndex: activeEntityIndex.value,
      generatedDocs: generatedDocs.value,
      conversationMessages: conversationMessages.value,
      skippedFields: Array.from(skippedFields.value),
      sectionSummaries: sectionSummaries.value,
      sectionSummarySources: sectionSummarySources.value,
      savedAt: savedAt.value,
    };
  }

  function fromJSON(state: DocGenState) {
    currentStep.value = state.currentStep;
    docType.value = state.docType;
    taskName.value = (state as any).taskName || '';

    // Multi-template restore (with legacy migration)
    if (state.templates?.length) {
      templates.value = state.templates;
    } else if (state.templateSource) {
      // Legacy single-template → migrate to array
      templates.value = [{
        source: state.templateSource,
        templateId: state.templateId,
        uploadId: state.uploadedFileId,
        fileName: state.templateName || 'Unknown',
      }];
    } else {
      templates.value = [];
    }

    variableSchema.value = state.variableSchema;
    entities.value = state.entities;
    activeEntityIndex.value = state.activeEntityIndex;
    generatedDocs.value = state.generatedDocs;
    conversationMessages.value = state.conversationMessages;
    skippedFields.value = new Set(state.skippedFields || []);  // Array → Set 복원
    sectionSummaries.value = state.sectionSummaries || {};
    sectionSummarySources.value = state.sectionSummarySources || {};
    savedAt.value = state.savedAt;
  }

  return {
    // State
    currentStep,
    docType,
    taskName,
    taskId,
    sessionEntityCd,
    templates,
    templateName,
    variableSchema,
    sectionSummaries,
    sectionSummarySources,
    entities,
    activeEntityIndex,
    selectedEntityProfile,
    cddHintData,
    generatedDocs,
    editorVisited,
    hasTSign,
    tSignAllComplete,
    eFilingCompleted,
    skippedFields,
    activeSkippedFields,
    conversationMessages,
    currentDraftId,
    drafts,
    loadingDrafts,
    variableHistory,
    loadingHistory,
    savedAt,
    saving,
    restoring,
    hasRestoredDraft,
    incorporationQA,
    incorporationMappedDocs,

    // Getters
    activeEntity,
    entityCount,
    hasVariables,
    canGenerate,

    // Actions
    setDocType,
    addTemplate,
    removeTemplate,
    confirmTemplates,
    setVariableSchema,
    addEntityForm,
    removeEntityForm,
    removeEntityTemplate,
    updateEntityFormData,
    updateEntityRepeatingCell,
    updateEntityInfo,
    setGeneratedDocs,
    skipField,
    goToStep,
    goBack,
    proceedToMultiEntity,
    proceedToGenerate,
    reset,
    saveDraft,
    tryRestore,
    loadDraftList,
    loadDraft,
    deleteDraft,
    loadVariableHistory,
    toJSON,
    fromJSON,
  };
});
